import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import { z } from "zod";

export type EntityMemberJSON = z.infer<typeof EntityMemberSchema.EntityMemberJSONZ>;
export type EntityMemberParams = z.infer<typeof EntityMemberSchema.EntityMemberZ>;

export default class EntityMemberSchema {
  public static readonly EntityMemberJSONZ = z.object({
    legalEntityId: z.uuid(),
    userId: z.uuid(),
    replacesId: z.uuid().nullish(),
  });

  public static readonly EntityMemberZ = z.object({
    legalEntityId: z.instanceof(Guid),
    userId: z.instanceof(Guid),
    replacesId: z.instanceof(Guid).optional(),
  });

  public static validate(values: unknown): EntityMemberParams {
    return parseOrThrow(EntityMemberSchema.EntityMemberZ, values, {
      logError: true,
      schemaName: "EntityMemberSchema",
      operation: "Validating EntityMemberParams",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): EntityMemberJSON {
    return parseOrThrow(EntityMemberSchema.EntityMemberJSONZ, json, {
      logError: true,
      schemaName: "EntityMemberSchema",
      operation: "Validating EntityMemberJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is EntityMemberParams {
    try {
      EntityMemberSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is EntityMemberJSON {
    try {
      EntityMemberSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: EntityMemberJSON): EntityMemberParams {
    if (isNullOrUndefined(data)) return data;
    const parsed = parseOrThrow(EntityMemberSchema.EntityMemberJSONZ, data, {
      logError: true,
      schemaName: "EntityMemberSchema",
      operation: "Deserializing EntityMemberJSON",
      payloadSample: data,
    });
    return EntityMemberSchema.codec.decode(parsed);
  }

  public static deserializeArray(data: EntityMemberJSON[]): EntityMemberParams[] {
    if (isNullOrUndefined(data)) return data;
    return data.map((item) => EntityMemberSchema.deserialize(item));
  }

  public static serialize(entityMember: EntityMemberParams): EntityMemberJSON {
    if (isNullOrUndefined(entityMember)) return entityMember;
    const parsed = EntityMemberSchema.validate(entityMember);
    if (isNullOrUndefined(parsed)) return parsed;
    return EntityMemberSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    EntityMemberSchema.EntityMemberJSONZ, 
    EntityMemberSchema.EntityMemberZ, {
    decode: (data) => ({
      legalEntityId: new Guid(GuidSchema.deserialize(data.legalEntityId, true), { allowEmpty: true }),
      userId: Guid.fromJSON(data.userId)!,
      replacesId: Guid.fromJSON(data.replacesId) ?? undefined,
    }),
    encode: (entityMember) => ({
      legalEntityId: entityMember.legalEntityId.toJSON(),
      userId: entityMember.userId.toJSON(),
      replacesId: entityMember.replacesId?.toJSON(),
    }),
  });
}
